import {CLStore} from 'common/src/redux/store';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {addOrUpdateCardInputSchema} from '../../../../schemas/baseSchemas';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import standaloneAgent from 'common/src/modules/shared/copilot/utils/creators/standaloneAgent';
import {responseSchema} from '../../promptToCardAgentV2.schemas';
import getSetupAgentModel from '../../../../utils/getSetupAgentModel';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {cardTypeToName} from '../../../../campaignSetupAgent.config';
import getPromptToCardAgentInstructions from '../../../promptToCardAgent/utils/getPromptToCardAgentInstructions';
import setCardNameTool from '../general/setCardNameTool';
import setCardDescriptionTool from '../general/setCardDescriptionTool';
import getFilesTool from 'common/src/modules/shared/copilot/tools/getFilesTool';
import readValuesFromExcelTool from '../../../../tools/general/readValuesFromExcelTool/readValuesFromExcelTool';
import searchSignalTool from '../conditions/searchSignalTool';
import deleteOutputsTool from '../general/deleteOutputsTool';
import getCardDetailsTool from '../general/getCardDetailsTool';
import preProcessStandaloneAgent from '../../utils/general/preProcessStandaloneAgent';
import postProcessStandaloneAgent from '../../utils/general/postProcessStandaloneAgent';
import {CompoundConditionCardScratchpadState} from '../../scratchpad/compoundConditionCardScratchpad.types';
import getOrCreateCompoundConditionCard from '../../utils/compoundConditions/getOrCreateCompoundConditionCard';
import addCompoundConditionOutputTool from './addCompoundConditionOutputTool';
import editCompoundConditionOutputTool from './editCompoundConditionOutputTool';
import {FallbackOutputGuidelines} from '../../promptToCardAgentV2.consts';

const cardSpecificInstructions = `1. Use the ${V2CardAgentToolName.SearchSignal} tool to find matching signals for the rules in the user's request.
2. Each output contains one or more **sections** (evaluated with AND logic between sections).
3. Each section contains one or more **rules** (evaluated with OR logic between rules within a section).
4. Each rule consists of a **signal**, an **operator**, and a **value**.
5. Use the ${V2CardAgentToolName.AddCompoundConditionOutput} tool to add new outputs with their sections and rules.
6. Use the ${V2CardAgentToolName.EditCompoundConditionOutput} tool to update existing outputs.
7. When setting outputs, DO NOT change existing outputs unless the user has explicitly requested changes.

### Compound Condition card specific instructions
${FallbackOutputGuidelines}.

### IMPORTANT
- ALWAYS search for signals before adding or editing outputs so you have the correct signal keys and names.
- DO NOT add fallback outputs of any kind, only set the outputs requested by the user.
- If the user did not ask to change existing outputs, skip modifying them.`;

export default function addOrUpdateCompoundConditionCardToolV2(store: CLStore) {
  async function execute(
    params: ToolExecuteParams<
      typeof addOrUpdateCardInputSchema,
      CompoundConditionCardScratchpadState
    >,
  ) {
    const {toolUtils, callChain} = params;

    const {promptToUse, normalizedCardId: cardId} = preProcessStandaloneAgent(params, store);

    const standaloneAgentDef = standaloneAgent({
      name: V2CardAgentToolName.AddOrUpdateCompoundConditionCard,
      role: `Your purpose is to convert a natural language prompt to a ${
        cardTypeToName[NodeType.COMPOUND_CONDITION]
      } card`,
      instructions: getPromptToCardAgentInstructions({
        cardType: NodeType.COMPOUND_CONDITION,
        cardId,
        cardSpecificInstructions,
      }),
      tools: [
        setCardNameTool(),
        setCardDescriptionTool(),
        deleteOutputsTool(),
        getFilesTool(),
        getCardDetailsTool(),
        readValuesFromExcelTool(),

        // Compound condition tools
        searchSignalTool(store, NodeType.COMPOUND_CONDITION),
        addCompoundConditionOutputTool(),
        editCompoundConditionOutputTool(),
      ],
      responseSchema,
      model: getSetupAgentModel(),
    });

    const toolScratchpad = {card: await getOrCreateCompoundConditionCard(cardId, store)};

    const result = await toolUtils.invokeStandaloneAgent({
      agent: standaloneAgentDef,
      prompt: promptToUse,
      callChain,
      scratchpad: toolScratchpad,
    });

    return postProcessStandaloneAgent({
      result: {status: result.status, chainOfThoughts: result.chainOfThoughts},
      scratchpad: toolScratchpad,
      store,
      cardId,
    });
  }

  return tool({
    name: V2CardAgentToolName.AddOrUpdateCompoundConditionCard,
    description:
      'Add or update a Compound Condition card. Supports multiple outputs, each with sections (AND logic) containing rules (OR logic). Each rule has a signal, operator, and value. This tool should be used for complex (NOT SINGULAR) conditions.',
    displayName: 'Working on compound condition card',
    inputSchema: addOrUpdateCardInputSchema,
    execute,
  });
}
